import { Component, Input, OnInit, OnDestroy, signal, computed, inject, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { forkJoin, Subscription } from 'rxjs';
import {
  ParkingTypeService,
  ParkingRightService,
  ParkingProcessService,
  ParkingProcessListItemDto,
  SortOrder,
  ParkingTypeDto,
  ListParkingTypeRequest,
  ParkingTypeEnum,
  CustomExemptionDto,
  DefaultExemptionDto,
  AssignedZoneParkersDto,
  PoolParkingDto,
  LongTermParkersDto,
  ShortTermParkingDto,
  EasypassDto,
  EasypassPaymentBehavior,
  DeleteAssignedZoneParkersRequest,
  DeletePoolParkingRequest,
  DeleteLongTermParkersRequest,
  DeleteShortTermParkersRequest,
  ListDefaultExemptionParkingTypeRequest,
  ListCustomExemptionParkingTypeRequest,
  ListAssignedZoneParkersRequest,
  ListPoolParkingRequest,
  ListLongTermParkersRequest,
  ListShortTermParkersRequest,
  ListEasypassesRequest,
  ParkingRightDto,
  ParkingSpaceDto
} from '../../../api/gen';
import { ResponsiveBaseComponent, JJamesBadgeComponent, JJamesPaginatorComponent, JJamesPaginationConfig, Role } from 'shared-lib';
import { MatTooltipModule } from '@angular/material/tooltip';
import { QRCodeComponent } from 'angularx-qrcode';
import { TranslatePipe } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { ParkingTypeItemComponent } from './parking-type-item/parking-type-item.component';
import { ParkingTypeAddComponent } from './parking-type-add/parking-type-add.component';
import { enabledIssueModes, IssueEasypassDialogComponent } from './issue-easypass/issue-easypass-dialog.component';
import { openExitTicketPrint } from './exit-ticket-print/exit-ticket-print-dialog.component';
import { RoleService } from '../../auth/role-service';

// Union type for all parking type DTOs
export type ParkingTypeUnion = DefaultExemptionDto | CustomExemptionDto | AssignedZoneParkersDto | PoolParkingDto | LongTermParkersDto | ShortTermParkingDto | EasypassDto;

@Component({
  selector: 'app-parking-type-space',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatSnackBarModule,
    MatPaginatorModule,
    MatTooltipModule,
    JJamesBadgeComponent,
    JJamesPaginatorComponent,
    ParkingTypeItemComponent,
    QRCodeComponent,
    TranslatePipe
  ],
  templateUrl: './parking-type-space.component.html',
  styleUrl: './parking-type-space.component.scss'
})
export class ParkingTypeSpaceComponent extends ResponsiveBaseComponent implements OnInit, OnDestroy {
  private parkingTypeService = inject(ParkingTypeService);
  private parkingRightService = inject(ParkingRightService);
  private parkingProcessService = inject(ParkingProcessService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private roleService = inject(RoleService);

  @Input({ required: true }) parkingSpace!: ParkingSpaceDto;
  @Input({ required: true }) parkingOperatorId: string | undefined;
  @Output() typeAdded = new EventEmitter<void>();

  /** Creating a parking type (Pool/ShortTerm/LongTerm/Easypass/exemptions) is Administrator-only. */
  isAdmin = signal(false);

  // Data - storing all types with their source info
  defaultExemptions = signal<DefaultExemptionDto[]>([]);
  customExemptions = signal<CustomExemptionDto[]>([]);
  assignedZoneParkers = signal<AssignedZoneParkersDto[]>([]);
  poolParkingTypes = signal<PoolParkingDto[]>([]);
  longTermParkers = signal<LongTermParkersDto[]>([]);
  shortTermParking = signal<ShortTermParkingDto[]>([]);
  easypassTypes = signal<EasypassDto[]>([]);

  // Combined parking types for display
  allParkingTypes = signal<ParkingTypeUnion[]>([]);

  // State
  isLoading = signal(false);

  // Easypass is collapsed to a single catalog row (like Pool); its issued passes
  // live in their own expandable list below the generic type list.
  // `easypassEnabled` = the space has its single Easypass availability ParkingType;
  // passes can only be issued once enabled.
  easypassEnabled = signal(false);
  showIssuedEasypasses = signal(false);

  // Invitations are paged and searched server-side (title or redeemed plate) — an event site can
  // hold hundreds, and each row carries every redemption.
  easypassSearch = signal('');
  easypassPageIndex = signal(0);
  easypassPageSize = signal(10);
  /** Matches for the current search — drives the paginator. */
  easypassTotal = signal(0);
  /** Every invitation on the space, regardless of the search — drives the header count. */
  easypassCount = signal(0);
  easypassLoading = signal(false);
  easypassPagination = computed<JJamesPaginationConfig>(() => {
    const total = this.easypassTotal();
    const size = this.easypassPageSize();
    return {
      currentPage: this.easypassPageIndex(),
      totalPages: Math.max(1, Math.ceil(total / size)),
      pageSize: size,
      totalItems: total,
      itemsPerPage: size,
      pageSizeOptions: [10, 25, 50]
    };
  });
  private easypassSearchTimer?: ReturnType<typeof setTimeout>;
  private easypassRequest?: Subscription;
  qrVisiblePassId = signal<string | null>(null);

  // Stays (parking processes) of the plates that redeemed an invitation. Keyed by invitation id and
  // filled on first expand — see togglePassStays.
  staysVisiblePassId = signal<string | null>(null);
  staysByPass = signal<Map<string, ParkingProcessListItemDto[]>>(new Map());
  staysLoading = signal(false);
  // redeemedParkingRightId -> license plate, resolved from the space's rights (enriched).
  redeemedPlateByRightId = signal<Record<string, string>>({});

  // Pagination — the six catalog endpoints are fetched once, at the backend's maximum page
  // size (100), and the merged list is paginated client-side. Server-side paging is not
  // possible across six independent collections behind a single paginator.
  private static readonly FetchPageSize = 100;

  totalCount = signal(0);
  pageSize = signal(10);
  pageIndex = signal(0);

  /** The merged catalog, sliced to the current page. */
  pagedParkingTypes = computed(() => {
    const start = this.pageIndex() * this.pageSize();
    return this.allParkingTypes().slice(start, start + this.pageSize());
  });

  async ngOnInit(): Promise<void> {
    // The role decides what is fetched, so it is read first.
    this.isAdmin.set(await this.roleService.checkAccess(Role.Administrator));
    this.loadParkingTypes();
  }

  loadParkingTypes(): void {
    if (!this.parkingSpace?.id) return;

    this.isLoading.set(true);
    this.loadEasypasses();

    // The parking-type catalog is shown to Administrators only; everyone else sees the Easypass
    // section, which needs nothing but the space's rights (for the redeemed plates).
    if (!this.isAdmin()) {
      this.loadRedeemedPlates();
      return;
    }

    // Common request parameters
    const defaultExemptionRequest: ListDefaultExemptionParkingTypeRequest = {
      pageSize: ParkingTypeSpaceComponent.FetchPageSize,
      pageIndex: 0,
      availableForParkingSpaces: [this.parkingSpace.id],
      operatorId: this.parkingOperatorId,
      includeDeleted: false
    };

    const customExemptionRequest: ListCustomExemptionParkingTypeRequest = {
      pageSize: ParkingTypeSpaceComponent.FetchPageSize,
      pageIndex: 0,
      availableForParkingSpaces: [this.parkingSpace.id],
      operatorId: this.parkingOperatorId,
      includeDeleted: false
    };

    const assignedZoneParkersRequest: ListAssignedZoneParkersRequest = {
      pageSize: ParkingTypeSpaceComponent.FetchPageSize,
      pageIndex: 0,
      availableForParkingSpaces: [this.parkingSpace.id],
      operatorId: this.parkingOperatorId,
      includeDeleted: false
    };

    const poolParkingRequest: ListPoolParkingRequest = {
      pageSize: ParkingTypeSpaceComponent.FetchPageSize,
      pageIndex: 0,
      availableForParkingSpaces: [this.parkingSpace.id],
      operatorId: this.parkingOperatorId,
      includeDeleted: false
    };

    const longTermRequest: ListLongTermParkersRequest = {
      pageSize: ParkingTypeSpaceComponent.FetchPageSize,
      pageIndex: 0,
      availableForParkingSpaces: [this.parkingSpace.id],
      operatorId: this.parkingOperatorId,
      includeDeleted: false
    };

    const shortTermRequest: ListShortTermParkersRequest = {
      pageSize: ParkingTypeSpaceComponent.FetchPageSize,
      pageIndex: 0,
      availableForParkingSpaces: [this.parkingSpace.id],
      operatorId: this.parkingOperatorId,
      includeDeleted: false
    };

    // Call all type APIs in parallel
    forkJoin({
      defaultExemptions: this.parkingTypeService.parkingTypesDefaultExemptionsFilterPost(defaultExemptionRequest),
      customExemptions: this.parkingTypeService.parkingTypesCustomExemptionsFilterPost(customExemptionRequest),
      assignedZoneParkers: this.parkingTypeService.parkingTypesAssignedZoneParkersFilterPost(assignedZoneParkersRequest),
      poolParkingTypes: this.parkingTypeService.parkingTypesPoolParkingFilterPost(poolParkingRequest),
      longTermParkers: this.parkingTypeService.parkingTypesLongTermParkersFilterPost(longTermRequest),
      shortTermParking: this.parkingTypeService.parkingTypesShortTermParkersFilterPost(shortTermRequest),
      // Enriched rights for this space — used to show the redeemed plate on each consumed pass.
      spaceRights: this.parkingRightService.parkingRightsBySpaceParkingSpaceIdGet(this.parkingSpace.id, false, true)
    }).subscribe({
      next: (results) => {
        // Per-event invitation model: no per-space "enable" gate — invitations can always be created.
        this.easypassEnabled.set(true);
        this.setRedeemedPlates(results.spaceRights);
        // Store individual results
        this.defaultExemptions.set(results.defaultExemptions.items || []);
        this.customExemptions.set(results.customExemptions.items || []);
        this.assignedZoneParkers.set(results.assignedZoneParkers.items || []);
        this.poolParkingTypes.set(results.poolParkingTypes.items || []);
        this.longTermParkers.set(results.longTermParkers.items || []);
        this.shortTermParking.set(results.shortTermParking.items || []);

        // Combine all types for display — Easypass is intentionally EXCLUDED here:
        // every issued pass is its own ParkingType, so listing them all floods the
        // catalog. It is collapsed to a single row + a separate issued-passes list.
        const allTypes: ParkingTypeUnion[] = [
          ...(results.defaultExemptions.items || []),
          ...(results.customExemptions.items || []),
          ...(results.assignedZoneParkers.items || []),
          ...(results.poolParkingTypes.items || []),
          ...(results.longTermParkers.items || []),
          ...(results.shortTermParking.items || [])
        ];

        this.allParkingTypes.set(allTypes);

        // Calculate total count — Easypass counts as ONE type row (like Pool),
        // regardless of how many individual passes have been issued.
        const total = (results.defaultExemptions.items?.length || 0) +
          (results.customExemptions.items?.length || 0) +
          (results.assignedZoneParkers.items?.length || 0) +
          (results.poolParkingTypes.items?.length || 0) +
          (results.longTermParkers.items?.length || 0) +
          (results.shortTermParking.items?.length || 0) +
          (this.easypassEnabled() ? 1 : 0);
        this.totalCount.set(total);

        // Keep the current page valid after reloads (e.g. deleting the last row of the last page).
        const maxPageIndex = Math.max(0, Math.ceil(Math.max(0, allTypes.length) / this.pageSize()) - 1);
        if (this.pageIndex() > maxPageIndex) this.pageIndex.set(maxPageIndex);

        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error loading parking types:', error);
        this.snackBar.open('Failed to load parking types', 'Close', { duration: 3000 });
        this.isLoading.set(false);
      }
    });
  }

  /** Non-admin load: only the space's rights, for the plate shown on each redeemed pass. */
  private loadRedeemedPlates(): void {
    this.parkingRightService.parkingRightsBySpaceParkingSpaceIdGet(this.parkingSpace.id!, false, true).subscribe({
      next: (rights) => {
        this.easypassEnabled.set(true);
        this.setRedeemedPlates(rights);
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error loading parking rights:', error);
        this.snackBar.open('Failed to load parking types', 'Close', { duration: 3000 });
        this.isLoading.set(false);
      }
    });
  }

  /** Map redeemed ParkingRight -> its license plate (first plate on the right). */
  private setRedeemedPlates(rights: ParkingRightDto[] | null | undefined): void {
    const plateMap: Record<string, string> = {};
    for (const right of rights || []) {
      const plate = right.allowedLicensePlates?.[0]?.licensePlateDto?.fullNumber;
      if (right.id && plate) plateMap[right.id] = plate;
    }
    this.redeemedPlateByRightId.set(plateMap);
  }

  // Helper to get the ParkingTypeDto from any union type
  getParkingType(item: ParkingTypeUnion): ParkingTypeDto | undefined {
    return item.parkingType;
  }
  // Helper to identify the type category
  getTypeCategory(item: ParkingTypeUnion): 'default-exemption' | 'custom-exemption' | 'assigned-zone-parkers' | 'pool-parking' | 'long-term' | 'short-term' | 'easypass' {
    if ('licensePlateRegex' in item) {
      return 'default-exemption';
    } else if ('exemptionLicensePlates' in item) {
      return 'custom-exemption';
    } else if ('parkingTypeId' in item && !('exemptionLicensePlates' in item) && !('licensePlateRegex' in item)) {
      const typeEnum = item.parkingType?.type?.type;
      if (typeEnum === ParkingTypeEnum.PoolParking) {
        return 'pool-parking';
      }
      if (typeEnum === ParkingTypeEnum.LongTerm) {
        return 'long-term';
      }
      if (typeEnum === ParkingTypeEnum.ShortTerm) {
        return 'short-term';
      }
      if (typeEnum === ParkingTypeEnum.Easypass) {
        return 'easypass';
      }
    }
    return 'assigned-zone-parkers';
  }

  onPageChange(event: PageEvent): void {
    // Client-side paging over the already-fetched catalog — no reload needed.
    this.pageIndex.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
  }

  openAddParkingTypeDialog(): void {
    const dialogRef = this.dialog.open(ParkingTypeAddComponent, {
      width: '800px',
      height: 'auto',
      maxWidth: '95vw',
      data: {
        parkingSpace: this.parkingSpace,
        parkingOperatorId: this.parkingOperatorId
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadParkingTypes();
        this.typeAdded.emit();
        this.snackBar.open('Parking type added successfully', 'Close', { duration: 3000 });
      }
    });
  }

  onDeleteType(parkingTypeItem: ParkingTypeUnion): void {
    const typeCategory = this.getTypeCategory(parkingTypeItem);

    switch (typeCategory) {
      case 'assigned-zone-parkers':
        this.parkingTypeService.parkingTypesAssignedZoneParkersAssignedZoneParkersIdDelete(
          parkingTypeItem.id!,
          { operatorId: this.parkingOperatorId } as DeleteAssignedZoneParkersRequest
        ).subscribe({
          next: () => {
            this.snackBar.open('Assigned Zone Parkers type removed successfully', 'Close', { duration: 3000 });
            this.loadParkingTypes();
          },
          error: (error) => {
            console.error('Error removing Assigned Zone Parkers type:', error);
            this.snackBar.open('Failed to remove Assigned Zone Parkers type', 'Close', { duration: 3000 });
          }
        });
        break;

      case 'default-exemption':
        this.parkingTypeService.parkingTypesDefaultExemptionsDefaultExemptionParkingTypeIdDelete(
          parkingTypeItem.id!,
          { operatorId: this.parkingOperatorId } as DeleteAssignedZoneParkersRequest
        ).subscribe({
          next: () => {
            this.snackBar.open('Default Exemption type removed successfully', 'Close', { duration: 3000 });
            this.loadParkingTypes();
          },
          error: (error) => {
            console.error('Error removing Default Exemption type:', error);
            this.snackBar.open('Failed to remove Default Exemption type', 'Close', { duration: 3000 });
          }
        });
        break;

      case 'custom-exemption':
        this.parkingTypeService.parkingTypesCustomExemptionsCustomExemptionParkingTypeIdDelete(
          parkingTypeItem.id!,
          { operatorId: this.parkingOperatorId } as DeleteAssignedZoneParkersRequest
        ).subscribe({
          next: () => {
            this.snackBar.open('Custom Exemption type removed successfully', 'Close', { duration: 3000 });
            this.loadParkingTypes();
          },
          error: (error) => {
            console.error('Error removing Custom Exemption type:', error);
            this.snackBar.open('Failed to remove Custom Exemption type', 'Close', { duration: 3000 });
          }
        });
        break;

      case 'pool-parking':
        this.parkingTypeService.parkingTypesPoolParkingPoolParkingIdDelete(
          parkingTypeItem.id!,
          { operatorId: this.parkingOperatorId } as DeletePoolParkingRequest
        ).subscribe({
          next: () => {
            this.snackBar.open('Pool Parking type removed successfully', 'Close', { duration: 3000 });
            this.loadParkingTypes();
          },
          error: (error) => {
            console.error('Error removing Pool Parking type:', error);
            this.snackBar.open('Failed to remove Pool Parking type', 'Close', { duration: 3000 });
          }
        });
        break;

      case 'long-term':
        this.parkingTypeService.parkingTypesLongTermParkersLongTermParkersIdDelete(
          parkingTypeItem.id!,
          { operatorId: this.parkingOperatorId } as DeleteLongTermParkersRequest
        ).subscribe({
          next: () => {
            this.snackBar.open('Long-Term type removed successfully', 'Close', { duration: 3000 });
            this.loadParkingTypes();
          },
          error: (error) => {
            console.error('Error removing Long-Term type:', error);
            this.snackBar.open('Failed to remove Long-Term type', 'Close', { duration: 3000 });
          }
        });
        break;

      case 'short-term':
        this.parkingTypeService.parkingTypesShortTermParkersShortTermParkersIdDelete(
          parkingTypeItem.id!,
          { operatorId: this.parkingOperatorId } as DeleteShortTermParkersRequest
        ).subscribe({
          next: () => {
            this.snackBar.open('Short-Term type removed successfully', 'Close', { duration: 3000 });
            this.loadParkingTypes();
          },
          error: (error) => {
            console.error('Error removing Short-Term type:', error);
            this.snackBar.open('Failed to remove Short-Term type', 'Close', { duration: 3000 });
          }
        });
        break;
    }
  }

  // ---- Easypass (collapsed row + issued-passes list) ----

  ngOnDestroy(): void {
    clearTimeout(this.easypassSearchTimer);
    this.easypassRequest?.unsubscribe();
  }

  /** One page of invitations for the current search. A newer load cancels the one still in flight. */
  loadEasypasses(): void {
    if (!this.parkingSpace?.id) return;

    const search = this.easypassSearch().trim();
    // Scoped by the space alone. Filtering on operatorId hid every invitation not stamped with the
    // space's creator — all old-platform imports (no operator) and the cashier-issued Quick passes.
    const request: ListEasypassesRequest = {
      pageSize: this.easypassPageSize(),
      pageIndex: this.easypassPageIndex(),
      availableForParkingSpaces: [this.parkingSpace.id],
      includeDeleted: false,
      search: search || undefined
    };

    this.easypassRequest?.unsubscribe();
    this.easypassLoading.set(true);
    this.easypassRequest = this.parkingTypeService.parkingTypesEasypassesFilterPost(request).subscribe({
      next: (result) => {
        this.easypassTypes.set(result.items ?? []);
        this.easypassTotal.set(result.totalCount ?? 0);
        if (!search) this.easypassCount.set(result.totalCount ?? 0);

        // Keep the page valid after a revoke empties the last one.
        const maxPageIndex = Math.max(0, Math.ceil(this.easypassTotal() / this.easypassPageSize()) - 1);
        if (this.easypassPageIndex() > maxPageIndex) {
          this.easypassPageIndex.set(maxPageIndex);
          this.loadEasypasses();
          return;
        }
        this.easypassLoading.set(false);
      },
      error: (error) => {
        console.error('Error loading Easypass invitations:', error);
        this.snackBar.open('Failed to load Easypass invitations', 'Close', { duration: 3000 });
        this.easypassLoading.set(false);
      }
    });
  }

  /** Debounced, and ignores 1-character terms, so typing does not query per keystroke. */
  onEasypassSearchInput(value: string): void {
    this.easypassSearch.set(value);
    clearTimeout(this.easypassSearchTimer);
    if (value.trim().length === 1) return;
    this.easypassSearchTimer = setTimeout(() => {
      this.easypassPageIndex.set(0);
      this.loadEasypasses();
    }, 500);
  }

  onEasypassPageChange(event: { pageIndex: number; pageSize: number }): void {
    this.easypassPageIndex.set(event.pageIndex);
    this.easypassPageSize.set(event.pageSize);
    this.loadEasypasses();
  }

  toggleIssuedEasypasses(): void {
    this.showIssuedEasypasses.update(v => !v);
  }

  /** At least one Easypass mode is switched on for this space (Quick has its own button on the space card). */
  canIssueEasypass(): boolean {
    return enabledIssueModes(this.parkingSpace).length > 0;
  }

  openExitTicketPrint(item: EasypassDto): void {
    openExitTicketPrint(this.dialog, item, this.parkingOperatorId, this.parkingSpace?.timeZoneId);
  }

  openIssueEasypassDialog(): void {
    const dialogRef = this.dialog.open(IssueEasypassDialogComponent, {
      width: this.isHandset() ? '95vw' : '640px',
      maxWidth: this.isHandset() ? '95vw' : '680px',
      autoFocus: false,
      data: {
        parkingSpace: this.parkingSpace,
        parkingOperatorId: this.parkingOperatorId
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) this.loadParkingTypes();
    });
  }

  easypassEntriesLabel(item: EasypassDto): string {
    const n = item.allowedEntries ?? 0;
    if (n === 0) return 'Unlimited';
    return n === 1 ? '1 entry (single use)' : `${n} entries`;
  }

  easypassWindow(item: EasypassDto): string {
    const from = item.validFrom ? new Date(item.validFrom).toLocaleDateString() : null;
    const to = item.validTo ? new Date(item.validTo).toLocaleDateString() : null;
    if (!from && !to) return 'No expiry';
    if (from && to) return `${from} – ${to}`;
    return from ? `From ${from}` : `Until ${to}`;
  }

  easypassTokenShort(item: EasypassDto): string {
    const t = item.token || '';
    return t ? `${t.substring(0, 8)}…` : '—';
  }

  /** Internal note — desk passes carry it in `note`, catalog passes in `comment`. */
  internalNote(item: EasypassDto): string | null {
    return item.note?.trim() || item.comment?.trim() || null;
  }

  /** Public / guest-facing note shown on the invitation row. */
  guestNote(item: EasypassDto): string | null {
    return item.description?.trim() || null;
  }

  /** Plates that have redeemed this invitation (many, under the per-event model). */
  redeemedPlate(item: EasypassDto): string | null {
    const plates = (item.redemptions ?? [])
      .map(r => r.licensePlate)
      .filter((p): p is string => !!p);
    return plates.length ? plates.join(', ') : null;
  }

  /**
   * Who settles this invitation's stays. Anything but ParkerPays reads as org-paid, matching the
   * backend, which falls back to OrgPays when the behaviour is missing.
   */
  isParkerPays(item: EasypassDto): boolean {
    return item.paymentBehavior === EasypassPaymentBehavior.ParkerPays;
  }

  /** Vehicles (plates) that have redeemed this invitation — not gate entries. */
  easypassRedeemedCount(item: EasypassDto): number {
    return item.redemptionCount ?? (item.redemptions?.length ?? 0);
  }

  /** Plate slots still free on a capped invitation. */
  easypassLeftCount(item: EasypassDto): number {
    return item.remainingCapacity ?? Math.max(0, (item.maxRedemptions ?? 0) - this.easypassRedeemedCount(item));
  }

  /** Raises the plate capacity of an invitation (institute increases e.g. 100 → 200). */
  onIncreaseCapacity(item: EasypassDto): void {
    if (!item.id) return;
    if (item.isUnlimited) {
      this.snackBar.open('This invitation is unlimited — no capacity to increase', 'Close', { duration: 3000 });
      return;
    }
    const current = item.maxRedemptions ?? 0;
    const input = window.prompt(`New maximum number of license plates (current: ${current})`, String(current + 100));
    if (input == null) return;
    const newMax = parseInt(input, 10);
    if (isNaN(newMax) || newMax <= current) {
      this.snackBar.open('Enter a number greater than the current capacity', 'Close', { duration: 3000 });
      return;
    }
    this.parkingTypeService.parkingTypesEasypassesEasypassIdIncreaseCapacityPost(item.id, {
      newMaxRedemptions: newMax,
      operatorId: this.parkingOperatorId
    }).subscribe({
      next: () => {
        this.snackBar.open('Capacity increased', 'Close', { duration: 3000 });
        this.loadParkingTypes();
      },
      error: (err) => {
        const detail = err?.error?.error?.detail || err?.error?.error?.message;
        this.snackBar.open(detail || 'Failed to increase capacity', 'Close', { duration: 4000 });
      }
    });
  }

  easypassRedeemUrl(item: EasypassDto): string {
    const token = item.token;
    if (!token) return '';
    return `${environment.parkerUrl}/easypass/redeem/${token}`;
  }

  togglePassQr(passId: string): void {
    this.qrVisiblePassId.update(current => current === passId ? null : passId);
  }

  /**
   * Shows the stays made against one invitation — every entry and exit of every plate that redeemed
   * it. Loaded on demand rather than with the list: an event with hundreds of redemptions has more
   * stays than the list itself, and most rows are never expanded.
   */
  togglePassStays(pass: EasypassDto): void {
    const id = pass.id ?? '';
    if (this.staysVisiblePassId() === id) {
      this.staysVisiblePassId.set(null);
      return;
    }

    this.staysVisiblePassId.set(id);

    // Cached per invitation, so re-opening a row does not re-query.
    if (this.staysByPass().has(id)) return;

    this.staysLoading.set(true);
    this.parkingProcessService.parkingProcessEasypassEasypassIdPost(id, {
      pageIndex: 0,
      pageSize: 200,
      sortBy: 'StartedAt',
      sortOrder: SortOrder.Descending
    }).subscribe({
      next: result => {
        this.staysByPass.update(map => new Map(map).set(id, result.items ?? []));
        this.staysLoading.set(false);
      },
      error: () => {
        // An empty list is recorded so the row shows "no stays" rather than spinning forever; the
        // snackbar says what actually happened.
        this.staysByPass.update(map => new Map(map).set(id, []));
        this.staysLoading.set(false);
        this.snackBar.open('Could not load the stays for this invitation', 'Close', { duration: 3000 });
      }
    });
  }

  staysFor(pass: EasypassDto): ParkingProcessListItemDto[] {
    return this.staysByPass().get(pass.id ?? '') ?? [];
  }

  copyRedeemLink(item: EasypassDto): void {
    const url = this.easypassRedeemUrl(item);
    if (!url) return;
    navigator.clipboard?.writeText(url).then(
      () => this.snackBar.open('Redemption link copied', 'Close', { duration: 2000 }),
      () => this.snackBar.open('Failed to copy link', 'Close', { duration: 2000 })
    );
  }

  downloadPassQr(item: EasypassDto): void {
    const panel = document.querySelector(`[data-pass-id="${item.id}"]`);
    const canvas = panel?.querySelector('canvas') as HTMLCanvasElement | null;
    const img = panel?.querySelector('img') as HTMLImageElement | null;

    const dataUrl = canvas ? canvas.toDataURL('image/png') : img?.src;
    if (!dataUrl) {
      this.snackBar.open('QR not ready yet — open the QR panel and try again', 'Close', { duration: 3000 });
      return;
    }

    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `easypass-${(item.token ?? item.id ?? 'pass').substring(0, 8)}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  onDeleteEasypass(item: EasypassDto): void {
    if (!item.id) return;
    const confirmed = confirm('Revoke this Easypass? The QR token will no longer be redeemable.');
    if (!confirmed) return;

    // No operatorId, matching the list: an invitation listed here must also be revocable here.
    this.parkingTypeService.parkingTypesEasypassesEasypassIdDelete(
      item.id,
      {}
    ).subscribe({
      next: () => {
        this.snackBar.open('Easypass revoked', 'Close', { duration: 3000 });
        this.loadParkingTypes();
      },
      error: (error) => {
        console.error('Error revoking Easypass:', error);
        this.snackBar.open('Failed to revoke Easypass', 'Close', { duration: 3000 });
      }
    });
  }

  refresh(): void {
    this.loadParkingTypes();
  }
}
